import { encodeLabsReadout, type LabsReadoutHandoff } from '@/app/(auth)/sign-up/_utils/labs-arrival';
import { applicationRedirectUrl } from '@/app/(platform)/application/_utils/application-redirect-url';

const SIGN_UP_PATH = '/sign-up';

export interface SignUpParams {
  email?: string;
  externshipId?: number;
  guideSource: string;
  /** The marketing choice they already made here, so sign-up does not silently re-decide it. */
  newsletter?: boolean;
  /**
   * Which entry point on the guide page this reader came through (`embed`, `newsletter`,
   * `article`). Carried in `utm_medium` because that is written into the ACCOUNT RECORD at sign-up
   * (Clerk `unsafeMetadata`), and the account record is the one Labs number that does not depend on
   * the reader accepting cookies. Since the consent release of Sep 18, Mixpanel only sees readers
   * who clicked OK, so this is how accounts are split by entry point. `utm_term` would be the
   * natural home, but sign-up does not store it.
   */
  placement?: string;
  /**
   * Their result, so sign-up can show it back to them. Nothing personal: the archetype id, two
   * swipe counts, four slider positions and a rates score. The readout itself stays where it is.
   */
  readout?: LabsReadoutHandoff;
  vertical: string;
}

/** Where every action on this screen ends up. UTMs carry the guide, the track and what was clicked. */
export function signUpHref({
  email,
  externshipId,
  guideSource,
  newsletter,
  placement,
  readout,
  vertical,
}: SignUpParams): string {
  const params = new URLSearchParams({
    utm_campaign: 'labs_preview',
    utm_content: vertical,
    // `lab` alone before Sep 19. Every value still starts with `lab`, so a `starts with` filter reads
    // both eras as one series.
    utm_medium: placement ? `lab-${placement}` : 'lab',
    utm_source: guideSource,
    ...(readout ? encodeLabsReadout(readout) : {}),
  });
  if (email) {
    params.set('email', email);
  }
  if (externshipId) {
    // `externshipId` feeds the sign-up arrival rail; `redirect_url` carries it past the application to the paywall.
    params.set('externshipId', String(externshipId));
    params.set('redirect_url', applicationRedirectUrl(externshipId));
  }
  // Only when they have actually been asked. Absent means sign-up keeps its own default.
  if (newsletter !== undefined) {
    params.set('newsletter', newsletter ? '1' : '0');
  }
  return `${SIGN_UP_PATH}?${params.toString()}`;
}
